import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion, useInView, AnimatePresence } from "framer-motion";

const features = [
  {
    title: "Centralized Order Dashboard",
    icon: (
      <img
        src="/assets/features/order/icon1.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Monitor all orders from one place, no matter where they come from.",
      "View and manage orders from POS, delivery, QR code, and third-party services in real-time.",
      "Instant order syncing and automatic status updates across all systems.",
      "Track order progress from placement to preparation and delivery completion.",
    ],
    image: "/assets/features/order/of1.svg",
  },
  {
    title: "Multi-Channel Order Integration",
    icon: (
      <img
        src="/assets/features/order/icon2.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Connect and manage orders from diverse sales channels.",
      "Support for in-house QR code ordering, self-service kiosks, and direct dine-in requests.",
      "Seamless sync with Egora POS for unified order visibility and updates.",
    ],
    image: "/assets/features/order/of1.svg",
  },
  {
    title: "Smart Order Routing and Efficiency",
    icon: (
      <img
        src="/assets/features/order/icon3.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Automatically route incoming orders to relevant kitchen stations or delivery staff.",
      "Minimize wait times and improve kitchen throughput during peak hours.",
    ],
    image: "/assets/features/order/of1.svg",
  },
  {
    title: "Order Insights and Analytics",
    icon: (
      <img
        src="/assets/features/order/icon4.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Leverage data to fine-tune your operations.",
      "Analyze peak hours and order volume trends.",
      "Identify bottlenecks and optimize fulfillment strategies accordingly",
    ],
    image: "/assets/features/order/of4.svg",
  },

  // {
  //   title: "Payments and Digital Wallets",
  //   icon: (
  //     <img
  //       src="/assets/features/order/icon5.svg"
  //       alt="My Icon"
  //       style={{ width: 60, height: 60 }}
  //     />
  //   ),
  //   points: [
  //     "Integrated support for Raast, credit/debit cards, and in-person cash handling.",
  //     "Real-time syncing of payments with POS and order tracking.",
  //   ],
  //   image: "/assets/features/order/of5.svg",
  // },

  {
    title: "Staff Management Integration",
    icon: (
      <img
        src="/assets/features/order/icon5.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Track performance, manage roles, and integrate payroll functions.",
      "Ensure accountability and maintain quality of service.",
    ],
    image: "/assets/features/order/of5.svg",
  },
];

export default function KeyFeaturesCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const currentFeature = features[currentSlide];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev === 0 ? features.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev === features.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      ref={ref}
      sx={{
        backgroundColor: "#F08203",
        color: "#fff",
        borderRadius: 3,
        mx: 2,
        py: 6,
        mt: 4,
        position: "relative",
      }}
    >
      <Typography
        variant="h1"
        fontWeight="bold"
        gutterBottom
        sx={{ px: { xs: 2, sm: 6, lg: 10 } }}
      >
        Key Features
      </Typography>

      <Divider sx={{ backgroundColor: "#fff", mb: 2 }} />

      <Grid
        container
        spacing={4}
        alignItems="center"
        sx={{ px: { xs: 2, sm: 6, lg: 10 } }}
      >
        <AnimatePresence mode="wait">
          {isInView && (
            <>
              <Grid
                item
                xs={12}
                md={7}
                key={currentSlide}
                component={motion.div}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.6 }}
              >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  <Box>{currentFeature.icon}</Box>
                  <Typography variant="h5" fontWeight={600}>
                    {currentFeature.title}
                  </Typography>
                </Stack>

                <Stack spacing={2} pl={2}>
                  {currentFeature.points.map((point, index) => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      key={index}
                    >
                      <CheckCircleIcon fontSize="small" />
                      <Typography>{point}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>

              <Grid
                item
                xs={12}
                md={5}
                key={"image-" + currentSlide}
                component={motion.div}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  component="img"
                  src={currentFeature.image}
                  alt={currentFeature.title}
                  // sx={{ width: "100%" }}
                  sx={{
                    width: "100%",
                    maxWidth: 600,
                    height: 450,
                    objectFit: "contain",
                    mx: "auto",
                  }}
                />
              </Grid>
            </>
          )}
        </AnimatePresence>
      </Grid>

      {/* Arrows */}
      <Box sx={{ px: { xs: 2, sm: 6, lg: 10 }, position: "relative" }}>
        <Divider
          sx={{
            backgroundColor: "#fff",
            position: "absolute",
            top: "50%",
            left: { xs: 20, sm: 40 },
            right: { xs: 20, sm: 40 },
            zIndex: 0,
          }}
        />

        <Stack
          direction="row"
          spacing={2}
          mt={4}
          justifyContent={{ xs: "center", md: "center", lg: "flex-start" }}
          sx={{ position: "relative", zIndex: 1, ml: { xs: 0, sm: 4 } }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={currentSlide === 0}
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              px: 2,
              py: 1,
              borderRadius: "999px",
              "&:hover": { backgroundColor: "#eee" },
              "&.Mui-disabled": {
                backgroundColor: "#fff",
                color: "#ccc",
                opacity: 1,
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={currentSlide === features.length - 1}
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              px: 2,
              py: 1,
              borderRadius: "999px",
              "&:hover": { backgroundColor: "#eee" },
              "&.Mui-disabled": {
                backgroundColor: "#fff",
                color: "#ccc",
                opacity: 1,
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}
